import { __assign, __read } from "tslib";
import IconBadgeControl from '@office-start/icon-badge-control';
import IconButtonControl from '@office-start/icon-button-control';
import { getKey, KeyboardKey } from '@office-start/keystroke-handler-utility';
import { useRegisterTeachingUiTargetElement } from '@office-start/teaching-ui-service';
import classNames from 'classnames';
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import styles from './LeftNavControl.module.scss';
var LeftNavTileControl = forwardRef(function (_a, ref) {
    var _b, _c, _d, _e;
    var id = _a.id, path = _a.path, icon = _a.icon, iconImageUrl = _a.iconImageUrl, toolTip = _a.toolTip, svgClassName = _a.svgClassName, isSelected = _a.isSelected, color = _a.color, onClick = _a.onClick, renderPanel = _a.renderPanel, squareTiles = _a.squareTiles, selectedStateIcon = _a.selectedStateIcon, darkMode = _a.darkMode, target = _a.target, darkModeIcon = _a.darkModeIcon, changeColorInDarkMode = _a.changeColorInDarkMode, ariaRole = _a.ariaRole, highContrast = _a.highContrast, keyDown = _a.keyDown, tabIndex = _a.tabIndex, badgeObservable = _a.badgeObservable, badgeElement = _a.badgeElement, includeSeparator = _a.includeSeparator, useFluentTheme = _a.useFluentTheme;
    var iconValue = isSelected && selectedStateIcon
        ? selectedStateIcon
        : (darkMode || highContrast) && darkModeIcon
            ? darkModeIcon
            : icon
                ? icon
                : '';
    var svgClassValue = iconValue ? undefined : svgClassName;
    var iconColor = darkMode && changeColorInDarkMode ? 'white' : color;
    return (React.createElement("li", { className: classNames(styles['left-nav-tile-container'], (_b = {},
            _b[styles['left-nav-tile-container--square']] = squareTiles,
            _b[classNames('ms-bclb-g40-af', styles['left-nav__last-core-tile'])] = includeSeparator,
            _b)) },
        React.createElement("div", { className: classNames(styles['left-nav-tile'], { 'ms-bgc-nl-h ms-bgc-g50-a': !useFluentTheme }, { 'ms-bgc-nlr-h ms-bgc-nta-a': useFluentTheme }, { 'ms-bgc-w': isSelected }, (_c = {},
                _c[styles['left-nav-tile--selected']] = isSelected && !squareTiles,
                _c[styles['left-nav-tile--square']] = squareTiles,
                _c[styles['left-nav-tile--selected--square']] = squareTiles && isSelected,
                _c[styles['left-nav-tile--selected--panel']] = isSelected && renderPanel && !squareTiles,
                _c)), style: { color: "".concat(color) } },
            React.createElement(IconButtonControl, { id: id, url: path, iconClass: classNames(svgClassValue, (_d = {},
                    _d[classNames(styles['left-nav-icon'], 'ms-fcl-bl')] = !svgClassValue,
                    _d[styles['left-nav-icon--svg']] = !!svgClassValue,
                    _d[styles['left-nav-icon--square']] = squareTiles,
                    _d)), iconName: iconValue, onClick: onClick, label: squareTiles ? toolTip : undefined, ariaLabel: toolTip, iconImageUrl: iconImageUrl, target: target, iconColor: iconColor, ariaPressedState: isSelected ? true : false, ariaRole: ariaRole, keyDown: keyDown, ref: ref, tabIndex: tabIndex },
                badgeObservable && (React.createElement(IconBadgeControl, { liveBadge: badgeObservable })),
                badgeElement),
            !squareTiles ? (React.createElement("div", { className: classNames(styles['left-nav-tile__indicator'], (_e = {},
                    _e[classNames('ms-bgc-w', styles['left-nav-indicator--panel'])] = isSelected && renderPanel,
                    _e)) },
                React.createElement("div", { className: isSelected
                        ? styles['left-nav-tile__right-border-container']
                        : styles['none'] },
                    React.createElement("span", { className: styles['left-nav-tile__right-border'], style: { borderColor: "".concat(color) } })))) : null,
            React.createElement("div", { className: styles['tooltip'] }, !squareTiles && !isSelected ? (React.createElement("span", { className: classNames('ms-bgc-w ms-bclr-w-af ms-fcl-bl', highContrast
                    ? styles['tooltip-text-highContrast']
                    : styles['tooltip-text']), "aria-hidden": true }, toolTip)) : null))));
});
LeftNavTileControl.displayName = 'LeftNavTileControl';
var supportedKeys = new Set([
    KeyboardKey.ArrowUp,
    KeyboardKey.ArrowDown,
    KeyboardKey.Home,
    KeyboardKey.End,
]);
export var LeftNavControl = function (_a) {
    var nonAppTiles = _a.nonAppTiles, coreAppTiles = _a.coreAppTiles, otherAppTiles = _a.otherAppTiles, allAppsTile = _a.allAppsTile, defaultSelectedId = _a.defaultSelectedId, squareTiles = _a.squareTiles, darkMode = _a.darkMode, target = _a.target, highContrast = _a.highContrast, ariaLandmarkLabel = _a.ariaLandmarkLabel, useFluentTheme = _a.useFluentTheme;
    var _b = __read(useState(defaultSelectedId ? defaultSelectedId : ''), 2), selectedId = _b[0], setSelectedId = _b[1];
    var _c = __read(useState(defaultSelectedId ? defaultSelectedId : ''), 2), prevSelectedId = _c[0], setPrevSelectedId = _c[1];
    var _d = __read(useState(defaultSelectedId ? defaultSelectedId : ''), 2), activeTabId = _d[0], setActiveTabId = _d[1];
    var panelRef = useRef(null);
    useEffect(function () {
        setSelectedId(defaultSelectedId ? defaultSelectedId : '');
    }, [defaultSelectedId]);
    var closePanel = useCallback(function () {
        setSelectedId(prevSelectedId);
        setPrevSelectedId(defaultSelectedId ? defaultSelectedId : '');
    }, [prevSelectedId, defaultSelectedId]);
    var selectedPanelTile = useMemo(function () {
        var resultInNonAppTiles = nonAppTiles.find(function (tile) { return tile.id === selectedId && !!tile.renderPanel; });
        return resultInNonAppTiles;
    }, [nonAppTiles, selectedId]);
    var allTiles = useMemo(function () {
        return nonAppTiles
            .concat(coreAppTiles || [])
            .concat(otherAppTiles || [])
            .concat(allAppsTile);
    }, [nonAppTiles, coreAppTiles, otherAppTiles, allAppsTile]);
    var tileRefs = useRef([]);
    var setActiveIndex = useCallback(function (index) {
        var _a;
        if (index < allTiles.length &&
            tileRefs &&
            tileRefs.current &&
            tileRefs.current[index]) {
            (_a = tileRefs.current[index]) === null || _a === void 0 ? void 0 : _a.focus();
            setActiveTabId(allTiles[index].id);
        }
    }, [allTiles]);
    var getIndex = useCallback(function (tileId) {
        var tileIndex = -1;
        allTiles.some(function (item, index) {
            if (item.id === tileId) {
                tileIndex = index;
                return true;
            }
            return false;
        });
        return tileIndex;
    }, [allTiles]);
    var handleKey = useCallback(function (event, tileIndex) {
        var key = getKey(event);
        var limit = allTiles.length;
        if (!supportedKeys.has(key)) {
            return;
        }
        event.preventDefault();
        switch (key) {
            case KeyboardKey.ArrowUp:
                setActiveIndex((tileIndex - 1 + limit) % limit);
                break;
            case KeyboardKey.ArrowDown:
                setActiveIndex((tileIndex + 1) % limit);
                break;
            case KeyboardKey.Home:
                setActiveIndex(0);
                break;
            case KeyboardKey.End:
                setActiveIndex(limit - 1);
        }
    }, [allTiles.length, setActiveIndex]);
    var targetLink = target;
    var registerAppBarCreateCallout = useRegisterTeachingUiTargetElement('AppBar_CreateCenter');
    var registerLeftNavTuiElement = useCallback(function (id, ref) {
        switch (id) {
            case 'left-nav-create-center':
                registerAppBarCreateCallout(ref);
                break;
        }
    }, [registerAppBarCreateCallout]);
    var tileItemToControl = useCallback(function (tile, includeSeparator) {
        var onTileClick = function (ev) {
            if (ev) {
                ev.stopPropagation();
                ev.nativeEvent.stopImmediatePropagation();
            }
            if (tile.renderPanel && selectedId === tile.id) {
                closePanel();
            }
            else {
                tile.onClick && tile.onClick(ev);
                if (!tile.path && !selectedPanelTile) {
                    setPrevSelectedId(selectedId);
                }
                if (!tile.path || selectedPanelTile) {
                    setSelectedId(tile.id);
                }
            }
        };
        var handleKeyPress = function (event) {
            if (event) {
                handleKey(event, getIndex(tile.id));
            }
        };
        return (React.createElement(LeftNavTileControl, __assign({}, tile, { key: tile.id, onClick: onTileClick, isSelected: selectedId === tile.id, squareTiles: squareTiles, darkMode: darkMode, target: targetLink, highContrast: highContrast, keyDown: handleKeyPress, ref: function (el) {
                tileRefs.current[getIndex(tile.id)] = el;
                // Attempt to register tui element for the given tile
                registerLeftNavTuiElement(tile.id, el);
            }, tabIndex: activeTabId === tile.id ? 0 : -1, includeSeparator: includeSeparator, useFluentTheme: useFluentTheme })));
    }, [
        selectedId,
        squareTiles,
        closePanel,
        darkMode,
        targetLink,
        selectedPanelTile,
        highContrast,
        activeTabId,
        getIndex,
        handleKey,
        registerLeftNavTuiElement,
    ]);
    var tileItems = useCallback(function (tiles, includeSeparator) {
        var tileLength = tiles.length;
        return tiles.map(function (tile, i) {
            return tileItemToControl(tile, includeSeparator && i === tileLength - 1);
        });
    }, [tileItemToControl]);
    var firstSectionTiles = nonAppTiles.concat(coreAppTiles);
    var firstSectionItems = useMemo(function () { return tileItems(firstSectionTiles, true); }, [nonAppTiles, coreAppTiles, tileItems]);
    var otherAppTileItems = useMemo(function () { return tileItems(otherAppTiles, false); }, [otherAppTiles, tileItems]);
    var allAppsTileItem = useMemo(function () { return tileItemToControl(allAppsTile, false); }, [allAppsTile, tileItemToControl]);
    var handlePanelClose = useCallback(function (event) {
        var key = getKey(event);
        if (key === KeyboardKey.Escape) {
            event.stopPropagation();
            event.nativeEvent.stopImmediatePropagation();
            closePanel();
            setActiveIndex(getIndex(selectedId)); // return focus to left nav tile on escape
        }
    }, [closePanel, setActiveIndex, getIndex, selectedId]);
    var selectedRenderPanel = useMemo(function () {
        if (selectedPanelTile && selectedPanelTile.renderPanel) {
            return (React.createElement("div", { ref: panelRef, className: styles['panel-container'], onKeyDown: handlePanelClose }, selectedPanelTile.renderPanel(closePanel)));
        }
        return null;
    }, [selectedPanelTile, closePanel, handlePanelClose]);
    useEffect(function () {
        var clickListener = function (e) {
            if (!selectedPanelTile) {
                return;
            }
            if (panelRef.current &&
                e.target instanceof Node &&
                panelRef.current.contains(e.target)) {
                return;
            }
            closePanel();
        };
        var blurListener = function () {
            if (!selectedPanelTile) {
                return;
            }
            closePanel();
        };
        document.addEventListener('click', clickListener);
        window.addEventListener('blur', blurListener);
        return function () {
            document.removeEventListener('click', clickListener);
            window.removeEventListener('blur', blurListener);
        };
    }, [selectedPanelTile, closePanel, selectedId, panelRef]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: classNames(useFluentTheme
                ? styles['left-nav-container--fluent']
                : 'ms-bgc-nla', styles['left-nav-container']), role: "navigation", "aria-label": ariaLandmarkLabel },
            React.createElement("div", { className: styles['scroll-hider'] },
                React.createElement("ul", { className: styles['scrolling-content'] },
                    firstSectionItems,
                    otherAppTileItems)),
            React.createElement("div", { className: styles['left-nav__bottom-section'] }, allAppsTileItem)),
        selectedRenderPanel));
};
//# sourceMappingURL=LeftNavControl.js.map